import { useState, useCallback, useEffect, useMemo } from 'react';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getVersionAnalysisList } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import type { PageInfoType } from '@op/types/common';
import { useStableValue } from '@opm/utils/stableKey';

const DEFAULT_PAGE_INFO: PageInfoType = { page: 1, page_size: 10 };

export type TSortItem =
  | 'start_time'
  | 'release_date'
  | 'time_interval'
  | 'number_of_reviews'
  | 'positive_reviews'
  | 'negative_reviews'
  | 'positive_ratio'
  | 'negative_ratio'
  | 'chapter'
  | 'release'
  | 'patch';

interface UseVersionAnalysisListOptions {
  filter: Record<string, any>;
  /** 分组维度（配置游戏 Tab 模式下由外部传入） */
  groupBy?: string | null;
  /** 当前 tab 是否激活，仅激活时才发起请求（默认 true） */
  isSelected?: boolean;
  /** 排序状态（由父组件受控传入） */
  sortInfo: SortBy<any>;
}

export const useVersionAnalysisList = ({
  filter,
  groupBy,
  isSelected = true,
  sortInfo,
}: UseVersionAnalysisListOptions) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [maxReviews, setMaxReviews] = useState(0);
  const [pageInfo, setPageInfo] = useState<PageInfoType>(DEFAULT_PAGE_INFO);
  const { wrapperCancelRequest, loading } = useCancelRequestHook<any>();
  const stableFilter = useStableValue(filter);
  const fetchList = useCallback(async () => {
    if (!filter?.edition_unified_id) return;

    // 清空旧数据，loading 期间不展示残留
    setTableData([]);
    setTotal(0);

    try {
      const params: Record<string, any> = {
        page: pageInfo.page,
        page_size: pageInfo.page_size,
        sort_item: sortInfo.by,
        sort_model: sortInfo.order,
        group_by: groupBy,
        ...stableFilter,
      };

      const res = await wrapperCancelRequest(getVersionAnalysisList, params);

      setTableData(res?.list || []);
      setTotal(res?.total || 0);
      setMaxReviews(res?.max_reviews || 0);
    } catch (err) {}
  }, [stableFilter, pageInfo, sortInfo, groupBy]);

  // filter / 排序变化时重置分页（变化后由下方 effect 统一触发请求）
  useUpdateEffect(() => {
    setPageInfo(DEFAULT_PAGE_INFO);
  }, [stableFilter, sortInfo]);

  // 仅当 tab 激活时发起请求；激活/分页/排序/filter/groupBy 变化都会触发
  useEffect(() => {
    if (!isSelected) return;
    fetchList();
  }, [isSelected, fetchList]);

  return {
    tableData,
    total,
    maxReviews,
    loading,
    refresh: fetchList,
    pageInfo,
    setPageInfo,
  };
};
